<template>
    <div class="account-managers-page">
        <div class="page-header">
            <div class="header-left">
                <h1>Account Managers</h1>
                <p class="subtitle">Gérer les Account Managers et leurs vendeurs assignés</p>
            </div>
            <button class="add-btn" @click="showAddModal = true">
                <i class="material-icons">add</i>
                <span>Nouvel Account Manager</span>
            </button>
        </div>

        <!-- Search & Filters -->
        <div class="filters-bar">
            <div class="search-box">
                <i class="material-icons">search</i>
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="Rechercher un Account Manager..."
                    @input="debouncedSearch"
                />
            </div>
            <select v-model="countryFilter" class="filter-select" @change="loadAccountManagers">
                <option value="">Tous les pays</option>
                <option v-for="country in countries" :key="country.code" :value="country.code">
                    {{ country.name }}
                </option>
            </select>
            <select v-model="statusFilter" class="filter-select" @change="loadAccountManagers">
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="blocked">Bloqué</option>
            </select>
        </div>

        <!-- Stats Summary -->
        <div class="stats-summary">
            <div class="stat-item">
                <i class="material-icons">supervisor_account</i>
                <div class="stat-content">
                    <span class="stat-value">{{ totalAccountManagers }}</span>
                    <span class="stat-label">Account Managers</span>
                </div>
            </div>
            <div class="stat-item">
                <i class="material-icons">store</i>
                <div class="stat-content">
                    <span class="stat-value">{{ totalAssignedSellers }}</span>
                    <span class="stat-label">Vendeurs assignés</span>
                </div>
            </div>
            <div class="stat-item">
                <i class="material-icons">person_off</i>
                <div class="stat-content">
                    <span class="stat-value">{{ unassignedSellersCount }}</span>
                    <span class="stat-label">Vendeurs non assignés</span>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Chargement...</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="accountManagers.length === 0" class="empty-state">
            <i class="material-icons">supervisor_account</i>
            <h3>Aucun Account Manager</h3>
            <p>Créez votre premier Account Manager pour commencer</p>
            <button class="primary-btn" @click="showAddModal = true">
                <i class="material-icons">add</i>
                Créer un Account Manager
            </button>
        </div>

        <!-- Account Managers List -->
        <div v-else class="managers-grid">
            <div
                v-for="manager in accountManagers"
                :key="manager._id"
                class="manager-card"
                :class="{ blocked: manager.status === 'blocked' }"
            >
                <div class="card-header">
                    <div class="manager-avatar">
                        <span>{{ getInitial(manager) }}</span>
                    </div>
                    <div class="manager-info">
                        <h3 class="manager-name">{{ manager.fullName || manager.email }}</h3>
                        <span class="manager-email">{{ manager.email }}</span>
                    </div>
                    <div class="manager-actions">
                        <button class="icon-btn" @click="editManager(manager)" title="Modifier">
                            <i class="material-icons">edit</i>
                        </button>
                        <button class="icon-btn danger" @click="confirmDelete(manager)" title="Supprimer">
                            <i class="material-icons">delete</i>
                        </button>
                    </div>
                </div>

                <div class="card-body">
                    <div class="info-row">
                        <span class="label">Statut:</span>
                        <span class="status-badge" :class="manager.status">
                            {{ manager.status === 'active' ? 'Actif' : 'Bloqué' }}
                        </span>
                    </div>
                    <div class="info-row">
                        <span class="label">Pays:</span>
                        <span class="value">{{ formatCountries(manager.countries) }}</span>
                    </div>
                    <div class="info-row">
                        <span class="label">Vendeurs assignés:</span>
                        <span class="value seller-count">{{ manager.sellersCount || 0 }}</span>
                    </div>
                </div>

                <div class="card-footer">
                    <button class="action-btn" @click="viewSellers(manager)">
                        <i class="material-icons">people</i>
                        <span>Voir les vendeurs</span>
                    </button>
                    <button class="action-btn primary" @click="assignSellers(manager)">
                        <i class="material-icons">person_add</i>
                        <span>Assigner</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
            <button class="page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                <i class="material-icons">chevron_left</i>
            </button>
            <span class="page-info">Page {{ currentPage }} sur {{ totalPages }}</span>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
                <i class="material-icons">chevron_right</i>
            </button>
        </div>

        <!-- Add/Edit Modal -->
        <div v-if="showAddModal || showEditModal" class="modal-overlay" @click.self="closeModals">
            <div class="modal-content">
                <div class="modal-header">
                    <h3>{{ showEditModal ? 'Modifier Account Manager' : 'Nouvel Account Manager' }}</h3>
                    <button class="close-btn" @click="closeModals">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="form-group">
                        <label>Nom complet *</label>
                        <input type="text" v-model="formData.fullName" placeholder="Nom complet" />
                    </div>
                    <div class="form-group">
                        <label>Email *</label>
                        <input type="email" v-model="formData.email" placeholder="email@example.com" :disabled="showEditModal" />
                    </div>
                    <div class="form-group" v-if="!showEditModal">
                        <label>Téléphone *</label>
                        <input type="tel" v-model="formData.phone" placeholder="+212600000000" />
                    </div>
                    <div class="form-group">
                        <label>Pays assignés</label>
                        <div class="checkbox-group">
                            <label v-for="country in countries" :key="country.code" class="checkbox-label">
                                <input type="checkbox" :value="country.code" v-model="formData.countries" />
                                <span>{{ country.name }}</span>
                            </label>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>Statut</label>
                        <select v-model="formData.status">
                            <option value="active">Actif</option>
                            <option value="blocked">Bloqué</option>
                        </select>
                    </div>

                    <!-- Buttons inside modal body -->
                    <div class="form-actions">
                        <button class="cancel-btn" @click="closeModals">Annuler</button>
                        <button class="submit-btn" @click="saveManager" :disabled="saving">
                            {{ saving ? 'Enregistrement...' : (showEditModal ? 'Mettre à jour' : 'Créer') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- View Sellers Modal -->
        <div v-if="showSellersModal" class="modal-overlay" @click.self="showSellersModal = false">
            <div class="modal-content large">
                <div class="modal-header">
                    <h3>Vendeurs de {{ selectedManager && selectedManager.fullName }}</h3>
                    <button class="close-btn" @click="showSellersModal = false">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="loadingSellers" class="loading-state small">
                        <div class="spinner"></div>
                    </div>
                    <div v-else-if="managerSellers.length === 0" class="empty-state small">
                        <p>Aucun vendeur assigné</p>
                    </div>
                    <div v-else class="sellers-list">
                        <div v-for="seller in managerSellers" :key="seller._id" class="seller-item">
                            <div class="seller-avatar">
                                <span>{{ getInitial(seller) }}</span>
                            </div>
                            <div class="seller-info">
                                <span class="seller-name">{{ seller.fullName || seller.email }}</span>
                                <span class="seller-country">{{ seller.country }}</span>
                            </div>
                            <button class="unassign-btn" @click="unassignSeller(seller)" title="Désassigner">
                                <i class="material-icons">person_remove</i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Assign Sellers Modal -->
        <div v-if="showAssignModal" class="modal-overlay" @click.self="showAssignModal = false">
            <div class="modal-content large">
                <div class="modal-header">
                    <h3>Assigner des vendeurs à {{ selectedManager && selectedManager.fullName }}</h3>
                    <button class="close-btn" @click="showAssignModal = false">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="modal-body">
                    <div class="search-box modal-search">
                        <i class="material-icons">search</i>
                        <input
                            type="text"
                            v-model="sellerSearchQuery"
                            placeholder="Rechercher un vendeur..."
                            @input="debouncedSellerSearch"
                        />
                    </div>
                    <div class="filter-row">
                        <select v-model="sellerCountryFilter" @change="loadUnassignedSellers">
                            <option value="">Tous les pays</option>
                            <option v-for="country in countries" :key="country.code" :value="country.code">
                                {{ country.name }}
                            </option>
                        </select>
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="showOnlyUnassigned" @change="loadUnassignedSellers" />
                            <span>Non assignés uniquement</span>
                        </label>
                    </div>

                    <div v-if="loadingUnassignedSellers" class="loading-state small">
                        <div class="spinner"></div>
                    </div>
                    <div v-else-if="unassignedSellers.length === 0" class="empty-state small">
                        <p>Aucun vendeur trouvé</p>
                    </div>
                    <div v-else class="sellers-list selectable">
                        <div
                            v-for="seller in unassignedSellers"
                            :key="seller._id"
                            class="seller-item"
                            :class="{ selected: selectedSellers.includes(seller._id) }"
                            @click="toggleSellerSelection(seller._id)"
                        >
                            <div class="checkbox-wrapper">
                                <input type="checkbox" :checked="selectedSellers.includes(seller._id)" />
                            </div>
                            <div class="seller-avatar">
                                <span>{{ getInitial(seller) }}</span>
                            </div>
                            <div class="seller-info">
                                <span class="seller-name">{{ seller.fullName || seller.email }}</span>
                                <span class="seller-country">{{ seller.country }}</span>
                            </div>
                            <span v-if="seller.accountManager" class="already-assigned">
                                (Assigné à {{ seller.accountManager.fullName || 'autre AM' }})
                            </span>
                        </div>
                    </div>

                    <!-- Buttons inside modal body -->
                    <div class="form-actions">
                        <span class="selection-count">{{ selectedSellers.length }} vendeur(s) sélectionné(s)</span>
                        <button class="cancel-btn" @click="showAssignModal = false">Annuler</button>
                        <button
                            class="submit-btn"
                            @click="confirmAssignSellers"
                            :disabled="selectedSellers.length === 0 || assigning"
                        >
                            {{ assigning ? 'Assignation...' : 'Assigner' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
            <div class="modal-content small">
                <div class="modal-header">
                    <h3>Confirmer la suppression</h3>
                    <button class="close-btn" @click="showDeleteModal = false">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                <div class="modal-body">
                    <p>Êtes-vous sûr de vouloir supprimer l'Account Manager <strong>{{ selectedManager && selectedManager.fullName }}</strong> ?</p>
                    <p class="warning-text">
                        <i class="material-icons">warning</i>
                        Les vendeurs assignés seront désassignés automatiquement.
                    </p>

                    <!-- Buttons inside modal body -->
                    <div class="form-actions">
                        <button class="cancel-btn" @click="showDeleteModal = false">Annuler</button>
                        <button class="submit-btn danger" @click="deleteManager" :disabled="deleting">
                            {{ deleting ? 'Suppression...' : 'Supprimer' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
    name: 'AccountManagers',
    data() {
        return {
            accountManagers: [],
            loading: false,
            searchQuery: '',
            countryFilter: '',
            statusFilter: '',
            countries: [],
            currentPage: 1,
            pageSize: 12,
            totalPages: 1,
            totalAccountManagers: 0,
            totalAssignedSellers: 0,
            unassignedSellersCount: 0,

            // Add/Edit modal
            showAddModal: false,
            showEditModal: false,
            formData: {
                fullName: '',
                email: '',
                phone: '',
                countries: [],
                status: 'active'
            },
            saving: false,
            accountManagerRole: null,

            // View sellers modal
            showSellersModal: false,
            selectedManager: null,
            managerSellers: [],
            loadingSellers: false,

            // Assign sellers modal
            showAssignModal: false,
            unassignedSellers: [],
            loadingUnassignedSellers: false,
            selectedSellers: [],
            sellerSearchQuery: '',
            sellerCountryFilter: '',
            showOnlyUnassigned: true,
            assigning: false,

            // Delete modal
            showDeleteModal: false,
            deleting: false
        };
    },
    created() {
        this.debouncedSearch = debounce(this.loadAccountManagers, 300);
        this.debouncedSellerSearch = debounce(this.loadUnassignedSellers, 300);
    },
    async mounted() {
        await this.loadCountries();
        await this.loadAccountManagerRole();
        await this.loadAccountManagers();
        await this.loadStats();
    },
    methods: {
        async loadAccountManagerRole() {
            try {
                // Search by type AccountManager - the base controller search passes filters to Model.find()
                var response = await this.$server.search('roles', { type: 'AccountManager', isDeleted: null, limit: 10 });
                console.log('AccountManager role search response:', response);
                if (response && response.content && response.content.results && response.content.results.length > 0) {
                    this.accountManagerRole = response.content.results[0];
                    console.log('AccountManager role found:', this.accountManagerRole);
                } else {
                    // Fallback: try to find by name
                    var fallbackResponse = await this.$server.find('roles', { name: 'Account Manager' });
                    console.log('AccountManager role fallback response:', fallbackResponse);
                    if (fallbackResponse && fallbackResponse.content && fallbackResponse.content.length > 0) {
                        this.accountManagerRole = fallbackResponse.content[0];
                    } else if (fallbackResponse && fallbackResponse.content && fallbackResponse.content.results && fallbackResponse.content.results.length > 0) {
                        this.accountManagerRole = fallbackResponse.content.results[0];
                    } else {
                        console.error('AccountManager role not found');
                        if (this.$toast) this.$toast.error('Le rôle Account Manager n\'existe pas. Veuillez le créer d\'abord.');
                    }
                }
            } catch (error) {
                console.error('Failed to load AccountManager role:', error);
            }
        },

        async loadCountries() {
            try {
                var response = await this.$server.search('warehouses', { all: true, limit: 100 });
                if (response && response.content && response.content.results) {
                    var countriesMap = {};
                    response.content.results.forEach(function(warehouse) {
                        if (warehouse.country) {
                            countriesMap[warehouse.country] = {
                                code: warehouse.country,
                                name: warehouse.countryName || warehouse.name || warehouse.country
                            };
                        }
                    });
                    this.countries = Object.values(countriesMap).sort(function(a, b) {
                        return a.name.localeCompare(b.name);
                    });
                }
            } catch (error) {
                console.error('Failed to load countries:', error);
                this.countries = [];
            }
        },

        async loadAccountManagers() {
            this.loading = true;
            try {
                var params = {
                    type: 'AccountManager',
                    page: this.currentPage,
                    limit: this.pageSize
                };

                if (this.searchQuery) {
                    params.name = this.searchQuery;
                }
                if (this.countryFilter) {
                    params.countries = this.countryFilter;
                }
                if (this.statusFilter) {
                    params.status = this.statusFilter;
                }

                var response = await this.$server.find('users', params);
                if (response && response.content) {
                    this.accountManagers = response.content.results || [];
                    this.totalAccountManagers = response.content.total || 0;
                    this.totalPages = Math.ceil(this.totalAccountManagers / this.pageSize);

                    // Load sellers count for each account manager
                    await this.loadSellersCounts();
                }
            } catch (error) {
                console.error('Failed to load account managers:', error);
                if (this.$toast) this.$toast.error('Erreur lors du chargement');
            } finally {
                this.loading = false;
            }
        },

        async loadSellersCounts() {
            var self = this;
            var promises = this.accountManagers.map(function(manager) {
                return self.$server.search('users', {
                    type: 'Seller',
                    accountManager: manager._id,
                    limit: 1
                }).then(function(response) {
                    if (response && response.content) {
                        manager.sellersCount = response.content.total || 0;
                    }
                }).catch(function() {
                    manager.sellersCount = 0;
                });
            });
            await Promise.all(promises);
            // Force reactivity update
            this.accountManagers = this.accountManagers.slice();
        },

        async loadStats() {
            try {
                // Get total assigned sellers
                var sellersResponse = await this.$server.search('users', {
                    type: 'Seller',
                    accountManager: { $ne: null },
                    limit: 1
                });
                if (sellersResponse && sellersResponse.content) {
                    this.totalAssignedSellers = sellersResponse.content.total || 0;
                }

                // Get unassigned sellers count
                var unassignedResponse = await this.$server.search('users', {
                    type: 'Seller',
                    accountManager: null,
                    limit: 1
                });
                if (unassignedResponse && unassignedResponse.content) {
                    this.unassignedSellersCount = unassignedResponse.content.total || 0;
                }
            } catch (error) {
                console.error('Failed to load stats:', error);
            }
        },

        getInitial(user) {
            if (!user) return '?';
            var name = user.fullName || user.firstName || user.email || '';
            return name.charAt(0).toUpperCase();
        },

        formatCountries(countryCodes) {
            if (!countryCodes || countryCodes.length === 0) return 'Tous';
            var self = this;
            var countryNames = countryCodes.map(function(code) {
                var country = self.countries.find(function(c) { return c.code === code; });
                return country ? country.name : code;
            });
            if (countryNames.length > 2) {
                return countryNames.slice(0, 2).join(', ') + ' +' + (countryNames.length - 2);
            }
            return countryNames.join(', ');
        },

        editManager(manager) {
            this.selectedManager = manager;
            this.formData = {
                _id: manager._id,
                fullName: manager.fullName || '',
                email: manager.email || '',
                countries: manager.countries || [],
                status: manager.status || 'active'
            };
            this.showEditModal = true;
        },

        async saveManager() {
            if (!this.formData.fullName || !this.formData.email) {
                if (this.$toast) this.$toast.error('Veuillez remplir tous les champs obligatoires');
                return;
            }

            if (!this.showEditModal && !this.formData.phone) {
                if (this.$toast) this.$toast.error('Le numéro de téléphone est requis');
                return;
            }

            if (!this.showEditModal && !this.accountManagerRole) {
                if (this.$toast) this.$toast.error('Le rôle Account Manager n\'existe pas. Veuillez contacter l\'administrateur.');
                return;
            }

            this.saving = true;
            try {
                var data = {
                    fullName: this.formData.fullName,
                    email: this.formData.email,
                    countries: this.formData.countries,
                    status: this.formData.status
                };

                // Add phone for new users
                if (!this.showEditModal) {
                    data.phone = this.formData.phone;
                }

                if (this.showEditModal) {
                    data._id = this.formData._id;
                    await this.$server.update('users', data);
                    if (this.$toast) this.$toast.success('Account Manager mis à jour');
                } else {
                    // For create, use role ID instead of type - backend derives type from role
                    data.role = this.accountManagerRole._id;
                    console.log('Creating Account Manager with data:', data);
                    var response = await this.$server.create('users', data);
                    console.log('Create response:', response);
                    if (response && (response.status === 400 || response.status === 404)) {
                        var errorMsg = response.content || 'Erreur lors de la création';
                        if (typeof errorMsg === 'object') {
                            errorMsg = JSON.stringify(errorMsg);
                        }
                        throw new Error(errorMsg);
                    }
                    if (this.$toast) this.$toast.success('Account Manager créé. Un email avec le mot de passe a été envoyé.');
                }

                this.closeModals();
                await this.loadAccountManagers();
            } catch (error) {
                console.error('Failed to save account manager:', error);
                var errorMessage = 'Erreur lors de l\'enregistrement';
                if (error && error.message) {
                    errorMessage = error.message;
                }
                if (this.$toast) this.$toast.error(errorMessage);
            } finally {
                this.saving = false;
            }
        },

        confirmDelete(manager) {
            this.selectedManager = manager;
            this.showDeleteModal = true;
        },

        async deleteManager() {
            if (!this.selectedManager) return;

            this.deleting = true;
            try {
                // First, unassign all sellers from this Account Manager
                var sellersResponse = await this.$server.search('users', {
                    type: 'Seller',
                    accountManager: this.selectedManager._id,
                    limit: 1000
                });

                if (sellersResponse && sellersResponse.content && sellersResponse.content.results) {
                    var self = this;
                    var unassignPromises = sellersResponse.content.results.map(function(seller) {
                        return self.$server.update('users', {
                            _id: seller._id,
                            accountManager: null
                        });
                    });
                    await Promise.all(unassignPromises);
                }

                // Then delete the Account Manager
                await this.$server.delete('users', { _id: this.selectedManager._id });
                if (this.$toast) this.$toast.success('Account Manager supprimé');
                this.showDeleteModal = false;
                this.selectedManager = null;
                await this.loadAccountManagers();
                await this.loadStats();
            } catch (error) {
                console.error('Failed to delete account manager:', error);
                if (this.$toast) this.$toast.error('Erreur lors de la suppression');
            } finally {
                this.deleting = false;
            }
        },

        async viewSellers(manager) {
            this.selectedManager = manager;
            this.showSellersModal = true;
            this.loadingSellers = true;

            try {
                var response = await this.$server.search('users', {
                    type: 'Seller',
                    accountManager: manager._id,
                    limit: 100
                });
                if (response && response.content) {
                    this.managerSellers = response.content.results || [];
                }
            } catch (error) {
                console.error('Failed to load manager sellers:', error);
            } finally {
                this.loadingSellers = false;
            }
        },

        async unassignSeller(seller) {
            try {
                await this.$server.update('users', {
                    _id: seller._id,
                    accountManager: null
                });

                // Remove from list
                var index = this.managerSellers.findIndex(function(s) { return s._id === seller._id; });
                if (index > -1) {
                    this.managerSellers.splice(index, 1);
                }

                if (this.$toast) this.$toast.success('Vendeur désassigné');
                await this.loadAccountManagers();
                await this.loadStats();
            } catch (error) {
                console.error('Failed to unassign seller:', error);
                if (this.$toast) this.$toast.error('Erreur lors de la désassignation');
            }
        },

        assignSellers(manager) {
            this.selectedManager = manager;
            this.selectedSellers = [];
            this.sellerSearchQuery = '';
            this.sellerCountryFilter = '';
            this.showAssignModal = true;
            this.loadUnassignedSellers();
        },

        async loadUnassignedSellers() {
            this.loadingUnassignedSellers = true;
            try {
                var params = {
                    type: 'Seller',
                    limit: 50,
                    name: this.sellerSearchQuery || ''
                };

                if (this.showOnlyUnassigned) {
                    params.accountManager = null;
                }
                if (this.sellerCountryFilter) {
                    params.countries = this.sellerCountryFilter;
                }

                // Use find method which supports name search across fullName, email, phone, etc.
                var response = await this.$server.find('users', params);
                if (response && response.content && response.content.results) {
                    this.unassignedSellers = response.content.results;
                } else {
                    this.unassignedSellers = [];
                }
            } catch (error) {
                console.error('Failed to load unassigned sellers:', error);
                this.unassignedSellers = [];
            } finally {
                this.loadingUnassignedSellers = false;
            }
        },

        toggleSellerSelection(sellerId) {
            var index = this.selectedSellers.indexOf(sellerId);
            if (index > -1) {
                this.selectedSellers.splice(index, 1);
            } else {
                this.selectedSellers.push(sellerId);
            }
        },

        async confirmAssignSellers() {
            if (this.selectedSellers.length === 0 || !this.selectedManager) return;

            this.assigning = true;
            try {
                // Assign each seller to the manager
                var self = this;
                var promises = this.selectedSellers.map(function(sellerId) {
                    return self.$server.update('users', {
                        _id: sellerId,
                        accountManager: self.selectedManager._id
                    });
                });

                await Promise.all(promises);

                if (this.$toast) this.$toast.success(this.selectedSellers.length + ' vendeur(s) assigné(s)');
                this.showAssignModal = false;
                this.selectedSellers = [];
                await this.loadAccountManagers();
                await this.loadStats();
            } catch (error) {
                console.error('Failed to assign sellers:', error);
                if (this.$toast) this.$toast.error('Erreur lors de l\'assignation');
            } finally {
                this.assigning = false;
            }
        },

        closeModals() {
            this.showAddModal = false;
            this.showEditModal = false;
            this.formData = {
                fullName: '',
                email: '',
                phone: '',
                countries: [],
                status: 'active'
            };
        },

        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.loadAccountManagers();
            }
        }
    }
};
</script>

<style scoped>
.account-managers-page {
    padding: 24px;
}

.page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
}

.header-left h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0;
    color: #6b7280;
    font-size: 14px;
}

.add-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.add-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
}

.filters-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.search-box {
    position: relative;
    flex: 1;
    min-width: 250px;
}

.search-box i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 20px;
}

.search-box input {
    width: 100%;
    padding: 12px 12px 12px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
}

.search-box input:focus {
    border-color: #059669;
}

.filter-select {
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    background-color: white;
    cursor: pointer;
    outline: none;
    min-width: 150px;
}

.filter-select:focus {
    border-color: #059669;
}

.stats-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-item i {
    font-size: 32px;
    color: #059669;
}

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
}

.stat-label {
    font-size: 13px;
    color: #6b7280;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #9ca3af;
}

.loading-state.small,
.empty-state.small {
    min-height: 150px;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state h3 {
    margin: 0 0 8px;
    font-size: 18px;
    color: #6b7280;
}

.primary-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background-color: #059669;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    margin-top: 16px;
}

.managers-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
}

.manager-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.2s;
}

.manager-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.manager-card.blocked {
    opacity: 0.7;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.manager-avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 18px;
    flex-shrink: 0;
}

.manager-info {
    flex: 1;
    min-width: 0;
}

.manager-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.manager-email {
    font-size: 13px;
    color: #6b7280;
}

.manager-actions {
    display: flex;
    gap: 4px;
}

.icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.icon-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
}

.icon-btn.danger:hover {
    background-color: #fef2f2;
    color: #ef4444;
}

.card-body {
    padding: 16px 20px;
}

.info-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-row .label {
    font-size: 13px;
    color: #6b7280;
    width: 130px;
    flex-shrink: 0;
}

.info-row .value {
    font-size: 14px;
    color: #111827;
    font-weight: 500;
}

.seller-count {
    background-color: #ecfdf5;
    color: #059669;
    padding: 2px 10px;
    border-radius: 12px;
    font-weight: 600;
}

.status-badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.status-badge.active {
    background-color: #ecfdf5;
    color: #059669;
}

.status-badge.blocked {
    background-color: #fef2f2;
    color: #ef4444;
}

.card-footer {
    display: flex;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
}

.action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    border: 1px solid #e5e7eb;
    background-color: white;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn:hover {
    background-color: #f3f4f6;
}

.action-btn.primary {
    background-color: #059669;
    border-color: #059669;
    color: white;
}

.action-btn.primary:hover {
    background-color: #047857;
}

.action-btn i {
    font-size: 18px;
}

/* Modal styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.modal-content {
    background: #ffffff;
    border-radius: 16px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-content.large {
    max-width: 700px;
}

.modal-content.small {
    max-width: 400px;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.close-btn:hover {
    background-color: #f3f4f6;
    color: #111827;
}

.modal-body {
    padding: 20px 24px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
    flex-shrink: 0;
    background-color: #ffffff;
}

.form-group {
    margin-bottom: 16px;
}

.form-group label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
}

.form-group input,
.form-group select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
}

.form-group input:focus,
.form-group select:focus {
    border-color: #059669;
}

.form-group input:disabled {
    background-color: #f9fafb;
    color: #6b7280;
}

.form-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    max-height: 150px;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #374151;
    cursor: pointer;
}

.cancel-btn {
    padding: 10px 20px;
    border: 1px solid #e5e7eb;
    background-color: white;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
}

.cancel-btn:hover {
    background-color: #f3f4f6;
}

.submit-btn {
    padding: 10px 20px;
    border: none;
    background-color: #059669;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
    background-color: #047857;
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.submit-btn.danger {
    background-color: #ef4444;
}

.submit-btn.danger:hover:not(:disabled) {
    background-color: #dc2626;
}

/* Sellers list in modals */
.sellers-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 400px;
    overflow-y: auto;
}

.seller-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
    background-color: #f9fafb;
    transition: all 0.2s;
}

.sellers-list.selectable .seller-item {
    cursor: pointer;
}

.sellers-list.selectable .seller-item:hover {
    background-color: #f3f4f6;
}

.sellers-list.selectable .seller-item.selected {
    background-color: #ecfdf5;
    border: 1px solid #059669;
}

.checkbox-wrapper {
    flex-shrink: 0;
}

.seller-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
}

.seller-info {
    flex: 1;
    min-width: 0;
}

.seller-name {
    display: block;
    font-weight: 500;
    color: #111827;
    font-size: 14px;
}

.seller-country {
    font-size: 12px;
    color: #6b7280;
}

.unassign-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.unassign-btn:hover {
    background-color: #fef2f2;
    color: #ef4444;
}

.already-assigned {
    font-size: 12px;
    color: #f59e0b;
    font-style: italic;
}

.modal-search {
    margin-bottom: 16px;
}

.filter-row {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
}

.filter-row select {
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 14px;
}

.selection-count {
    margin-right: auto;
    font-size: 14px;
    color: #6b7280;
}

.warning-text {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #f59e0b;
    font-size: 14px;
    margin-top: 16px;
}

.warning-text i {
    font-size: 20px;
}

/* Pagination */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
}

.page-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: #6b7280;
}

/* Responsive */
@media (max-width: 768px) {
    .account-managers-page {
        padding: 16px;
    }

    .page-header {
        flex-direction: column;
        gap: 16px;
    }

    .add-btn {
        width: 100%;
        justify-content: center;
    }

    .filters-bar {
        flex-direction: column;
    }

    .search-box {
        min-width: 100%;
    }

    .filter-select {
        width: 100%;
    }

    .managers-grid {
        grid-template-columns: 1fr;
    }

    .card-footer {
        flex-direction: column;
    }
}
</style>
